import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicBookingService, quoteRequestsService } from '../../services/api';
import { sendVisit } from '../../utils/visitBeacon';
import './Booking.css';

// Same words as the Smart Quote section on the landing site.
const STEPS = [
  ['Send your address', "Tell us where, and tick what you'd like cleaned. Add a phone photo or two if you like."],
  ['We measure it', 'Smart Quote measures each area from satellite imagery, so nothing is guessed or rounded up.'],
  ['Your quote arrives', 'An itemised price by email, with every area marked on an aerial photo of your place.'],
  ['Accept online', "Happy with it? Accept with one tap and we'll be in touch to book a day."]
];

// "Send us your address" (GID-Session-022): a smart-quote request with no day
// or time to pick. Lands in Jobs as an enquiry; the quote comes back by email.
export default function QuoteRequest() {
  const [services, setServices] = useState([]);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({
    address_street: '', address_suburb: '', address_state: 'QLD', address_postcode: '',
    details: '', name: '', email: '', phone: ''
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { photoNote }

  useEffect(() => { sendVisit({ site: 'book' }); }, []);
  useEffect(() => {
    publicBookingService.listServices().then((r) => {
      // Job types that are priced by quote; "Free Quote Visit" is the other way to ask.
      if (r.success) setServices((r.services || []).filter((s) => s.price_type === 'quote'));
    });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  function addPhotos(fileList) {
    const incoming = [...fileList].filter((f) => f.type.startsWith('image/'));
    setPhotos((prev) => [...prev, ...incoming].slice(0, 5));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!form.address_street.trim() || !form.address_suburb.trim()) { setError('Please enter the property address (street and suburb).'); return; }
    if (!picked.length && !form.details.trim()) { setError("Tick what you'd like cleaned, or tell us in a few words."); return; }
    if (!form.name.trim()) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError('Please enter a valid email — that is where your quote goes.'); return; }
    if (!form.phone.trim()) { setError('Please enter your phone number.'); return; }
    setSubmitting(true);
    let src = null;
    try { src = sessionStorage.getItem('gid_src'); } catch { /* storage blocked */ }
    const r = await quoteRequestsService.create({ ...form, serviceIds: picked, src, ref: document.referrer || null });
    if (!r.success) { setSubmitting(false); setError(r.error || 'Sorry, that did not send. Please try again or call us.'); return; }
    let photoNote = '';
    if (photos.length) {
      const up = await quoteRequestsService.uploadImages(r.token, photos);
      photoNote = up.success
        ? `We got your ${photos.length} photo${photos.length === 1 ? '' : 's'} too.`
        : 'Your request is in, but the photos did not upload — you can reply to our email with them instead.';
    }
    sendVisit({ site: 'book', event: 'quote' });
    setSubmitting(false);
    setDone({ photoNote });
    window.scrollTo(0, 0);
  }

  if (done) {
    return (
      <div className="booking-card booking-done">
        <div className="done-check">✓</div>
        <h1>Thanks {form.name.trim().split(' ')[0]}, we've got it</h1>
        <p className="done-when">{[form.address_street, form.address_suburb].map((s) => s.trim()).join(', ')}</p>
        <p className="text-muted">We'll measure your place on the satellite map and email your itemised quote to {form.email.trim()}, usually within a day. If we need a closer look, we'll call you first.</p>
        {done.photoNote && <p className="text-muted">{done.photoNote}</p>}
        <div className="done-actions">
          <a className="btn btn-outline" href="https://getitdone.getit-media.com.au/#work">See our before &amp; afters</a>
        </div>
      </div>
    );
  }

  return (
    <form className="booking-wizard qr-page" onSubmit={submit} noValidate>
      <div className="booking-card">
        <p className="pq-kicker">Free Smart Quote · no visit needed</p>
        <h1>Send us your address. We'll send you a price.</h1>
        <ol className="qr-steps">
          {STEPS.map(([title, body], i) => (
            <li key={title}><span className="qr-step-num">{i + 1}</span><span><strong>{title}</strong><span className="text-muted">{body}</span></span></li>
          ))}
        </ol>
      </div>

      {error && <div className="booking-error">{error}</div>}

      <div className="booking-card">
        <h2 className="qr-h2">Where's the job?</h2>
        <div className="form-group"><label htmlFor="address_street">Property address *</label>
          <input id="address_street" placeholder="Street address" autoComplete="street-address" value={form.address_street} onChange={set('address_street')} /></div>
        <div className="form-row">
          <div className="form-group"><label htmlFor="address_suburb">Suburb *</label>
            <input id="address_suburb" autoComplete="address-level2" value={form.address_suburb} onChange={set('address_suburb')} /></div>
          <div className="form-group"><label htmlFor="address_state">State</label>
            <input id="address_state" value={form.address_state} onChange={set('address_state')} /></div>
          <div className="form-group"><label htmlFor="address_postcode">Postcode</label>
            <input id="address_postcode" inputMode="numeric" autoComplete="postal-code" value={form.address_postcode} onChange={set('address_postcode')} /></div>
        </div>

        <h2 className="qr-h2">What would you like cleaned?</h2>
        <div className="qr-picks">
          {services.map((s) => (
            <label key={s.id} className={`qr-pick ${picked.includes(s.id) ? 'active' : ''}`}>
              <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} />
              <span><strong>{s.name}</strong>{s.description && <span className="text-muted">{s.description}</span>}</span>
            </label>
          ))}
        </div>
        <div className="form-group"><label htmlFor="details">Anything else? (optional)</label>
          <textarea id="details" rows="3" placeholder="e.g. just the front driveway and the path down the side, two-storey house, the pool fence is glass…" value={form.details} onChange={set('details')} /></div>
        <div className="form-group">
          <label htmlFor="photos">Photos (optional, up to 5)</label>
          <p className="photo-hint">A couple of quick phone shots help — especially of stains or anything the satellite can't see under trees.</p>
          <input id="photos" type="file" accept="image/*" multiple onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
          {photos.length > 0 && (
            <div className="photo-thumbs">
              {photos.map((f, i) => (
                <div key={`${f.name}-${i}`} className="photo-thumb">
                  <img src={URL.createObjectURL(f)} alt={f.name} />
                  <button type="button" className="photo-remove" aria-label={`Remove ${f.name}`} onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className="qr-h2">Where should we send it?</h2>
        <div className="form-group"><label htmlFor="name">Name *</label>
          <input id="name" autoComplete="name" value={form.name} onChange={set('name')} /></div>
        <div className="form-row qr-contact">
          <div className="form-group"><label htmlFor="email">Email *</label>
            <input id="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} /></div>
          <div className="form-group"><label htmlFor="phone">Phone *</label>
            <input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} /></div>
        </div>
        <p className="booking-privacy-note">
          We only use your details to quote and arrange your job — see our{' '}
          <a href="https://getitdone.getit-media.com.au/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
        </p>
      </div>

      <div className="booking-actions">
        <button type="submit" className="btn btn-primary booking-confirm" disabled={submitting}>
          {submitting ? 'Sending…' : 'Get my free quote'}
        </button>
      </div>
      <p className="qr-alt text-muted">Rather we came and had a look? <Link to="/book">Book a free quote visit</Link>.</p>
    </form>
  );
}
